import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { FieldExplainer } from '@/components/calculator/FieldExplainer';
import type { FieldExplainerContent } from '@/content/field-explainers/types';
import { cn } from '@/lib/utils';

type ResultSummaryCardProps = {
  label: string;
  value: string;
  note: string;
  trend: 'up' | 'down' | 'flat';
  explainer?: FieldExplainerContent;
};

export function ResultSummaryCard({ label, value, note, trend, explainer }: ResultSummaryCardProps) {
  return (
    <Card className="brand-frame border-border/80 bg-card/92">
      <CardContent className="flex flex-col gap-2 p-4">
        <div className="flex items-center gap-1.5">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
          {explainer ? <FieldExplainer explainer={explainer} /> : null}
        </div>
        <p className="font-display text-2xl text-foreground">{value}</p>
        <p className="inline-flex items-center gap-2 text-xs text-muted-foreground">
          <Badge
            variant="outline"
            className={cn(
              'gap-1 border px-1.5 py-0.5 text-[10px] tracking-[0.16em]',
              trend === 'up' && 'border-emerald-500/40 text-emerald-600 dark:text-emerald-300',
              trend === 'down' && 'border-rose-500/40 text-rose-600 dark:text-rose-300',
              trend === 'flat' && 'border-border text-muted-foreground'
            )}
          >
            {trend === 'up' ? <ArrowUpRight className="h-3 w-3" aria-hidden="true" /> : null}
            {trend === 'down' ? <ArrowDownRight className="h-3 w-3" aria-hidden="true" /> : null}
            {trend === 'flat' ? <span aria-hidden="true">•</span> : null}
            <span className="sr-only">{trend === 'up' ? 'increasing' : trend === 'down' ? 'decreasing' : 'steady'}</span>
          </Badge>
          {note}
        </p>
      </CardContent>
    </Card>
  );
}
